import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import logger from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  User, Wrench, CreditCard, Zap, ArrowLeft,
  Loader2, ChevronRight, Plus, Trash2, Copy, AlertTriangle, ExternalLink,
  Truck, Camera, RotateCcw, Gift, Info
} from "lucide-react";
import { MONTHS, YEARS, BODY_TYPES } from "@/pages/NewJob";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Charger segment config ─────────────────────────────────────────────────
const CHARGER_SEGMENTS = [
  { label: "Small Charger",    icon: "🔋" },
  { label: "Fast Charger",     icon: "⚡" },
  { label: "Rickshaw Charger", icon: "🛺" },
];
const SEGMENT_MODELS = {
  "Small Charger":    ["54.6V / 6A","58.4V / 6A","67.2V / 6A","69.3V / 6A","71.2V / 6A","73V / 6A","84V / 5A"],
  "Fast Charger":     ["54.6V / 10A","58.4V / 10A","67.2V / 10A","68.4V / 10A","69.3V / 10A","71.2V / 10A","73V / 10A","84V / 10A"],
  "Rickshaw Charger": ["54.6V / 15A","58.4V / 15A","67.2V / 15A","69.3V / 15A","71.2V / 15A","73V / 15A","54.6V / 20A","58.4V / 20A","67.2V / 20A","69.3V / 20A","71.2V / 20A","73V / 20A"],
};

const EMPTY_COURIER = { courier_name: "", awb_number: "", tracking_url: "", dispatch_date: "", expected_return_date: "" };
const EMPTY_CHARGER = {
  charger_type: "", charger_spec: "", serial_number: "",
  purchase_month: "", purchase_year: "", body_type: "",
  xp_charger_status: "",
  outgoing_courier: { ...EMPTY_COURIER },
  incoming_courier: { ...EMPTY_COURIER },
  photo_url: "",
};

// Within 3 months from today?
const isEligibleForNew = (month, year) => {
  if (!month || !year) return false;
  const m = parseInt(month) - 1;
  if (isNaN(m)) return false;
  const purchase = new Date(parseInt(year), m, 1);
  const cutoff   = new Date();
  cutoff.setMonth(cutoff.getMonth() - 3);
  return purchase >= cutoff;
};

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-8 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
      <Icon size={15} className="text-white" />
    </div>
    <div>
      <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{title}</h3>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
    </div>
  </div>
);

const F = ({ label, required, error, children }) => (
  <div>
    <Label className="text-xs font-medium text-slate-700">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}
  </div>
);

const CourierFields = ({ value, onChange, outgoing }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
    <F label="Courier Name">
      <Input placeholder="e.g. Ekart, Amazon" value={value.courier_name}
        onChange={e => onChange({ ...value, courier_name: e.target.value })} className="h-9 text-sm" />
    </F>
    <F label="AWB Number">
      <Input placeholder="Tracking / AWB number" value={value.awb_number}
        onChange={e => onChange({ ...value, awb_number: e.target.value })} className="h-9 text-sm font-mono" />
    </F>
    <F label="Tracking Link (URL)">
      <Input placeholder="https://track.courier.com/..." value={value.tracking_url}
        onChange={e => onChange({ ...value, tracking_url: e.target.value })} className="h-9 text-sm" />
    </F>
    {outgoing
      ? <F label="Dispatch Date">
          <input type="date" value={value.dispatch_date}
            onChange={e => onChange({ ...value, dispatch_date: e.target.value })}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm h-9 focus:outline-none focus:border-green-400" />
        </F>
      : <F label="Expected Return Date">
          <input type="date" value={value.expected_return_date}
            onChange={e => onChange({ ...value, expected_return_date: e.target.value })}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm h-9 focus:outline-none focus:border-green-400" />
        </F>}
  </div>
);

export default function ServiceEntry() {
  const navigate = useNavigate();
  const phoneTimer = useRef(null);
  const [loading,   setLoading]   = useState(false);
  const [errors,    setErrors]    = useState({});

  // ── Customer ──────────────────────────────────────────────────────────────
  const [custName,  setCustName]  = useState("");
  const [phone,     setPhone]     = useState("");
  const [altPhone,  setAltPhone]  = useState("");
  const [address,   setAddress]   = useState("");

  const [existingCustomer, setExistingCustomer] = useState(null);
  const checkPhone = async (p) => {
    if (!p.trim() || p.trim().length < 8) { setExistingCustomer(null); return; }
    try {
      const r = await api.get(`${API}/customers/lookup?phone=${encodeURIComponent(p.trim())}`);
      setExistingCustomer(r.data.found ? r.data : null);
    } catch { setExistingCustomer(null); }
  };
  const handlePhone = (val) => {
    setPhone(val);
    clearTimeout(phoneTimer.current);
    phoneTimer.current = setTimeout(() => checkPhone(val), 600);
  };

  // ── Fault & Notes ─────────────────────────────────────────────────────────
  const [faultDesc,   setFaultDesc]   = useState("");
  const [techNotes,   setTechNotes]   = useState("");
  const [repairNotes, setRepairNotes] = useState("");

  // ── Payment ───────────────────────────────────────────────────────────────
  const [totalAmt,  setTotalAmt]  = useState("");
  const [paidAmt,   setPaidAmt]   = useState("");
  const [payMethod, setPayMethod] = useState("Cash");
  const due = Math.max(0, parseFloat(totalAmt||0) - parseFloat(paidAmt||0));

  // ── Chargers ──────────────────────────────────────────────────────────────
  const [chargers, setChargers] = useState([{ ...EMPTY_CHARGER }]);
  const addCharger  = () => setChargers(p => [...p, { ...EMPTY_CHARGER }]);
  const removeCharger = (i) => setChargers(p => p.filter((_, idx) => idx !== i));
  const duplicateCharger = (i) => setChargers(p => { const c = { ...p[i] }; const n = [...p]; n.splice(i+1,0,c); return n; });
  const updateCharger = (i, field, val) => setChargers(p => { const n = [...p]; n[i] = { ...n[i], [field]: val }; return n; });

  // ── Photo upload ───────────────────────────────────────────────────────────
  const [photoUploading, setPhotoUploading] = useState({});
  const handlePhoto = async (i, file) => {
    if (!file) return;
    const MAX = 20 * 1024 * 1024;
    if (file.size > MAX) { toast.error(`Photo too large — max 20 MB (yours: ${(file.size/1024/1024).toFixed(1)} MB)`); return; }
    setPhotoUploading(p => ({ ...p, [i]: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateCharger(i, "photo_url", r.data.url);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setPhotoUploading(p => ({ ...p, [i]: false })); }
  };

  // ── Validate & Submit ─────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!custName.trim()) e.name  = "Required";
    if (!phone.trim())    e.phone = "Required";
    chargers.forEach((c, i) => {
      if (!c.photo_url) e[`photo_${i}`] = "Required";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const products = chargers.map(c => ({
        charger_type:       c.charger_type,
        charger_spec:       c.charger_spec,
        serial_number:      c.serial_number || "",
        purchase_date:      [c.purchase_month, c.purchase_year].filter(Boolean).join(" "),
        body_type:          c.body_type,
        photo_url:          c.photo_url || "",
        xp_charger_status:  c.xp_charger_status || "",
        outgoing_courier:   c.xp_charger_status ? c.outgoing_courier : null,
        incoming_courier:   c.xp_charger_status ? c.incoming_courier : null,
      }));
      const res = await api.post(`${API}/jobs`, {
        customer: { name: custName.trim(), phone: phone.trim(), alt_phone: altPhone.trim(), address: address.trim() },
        products,
        specs: { fault_description: faultDesc, technical_notes: techNotes, repair_notes: repairNotes, problem_description: faultDesc },
        payment: { amount: parseFloat(totalAmt)||0, paid_amount: parseFloat(paidAmt)||0, method: payMethod },
      });
      toast.success(`✅ Job ${res.data.job_no} created!`);
      navigate(`/jobs/${res.data.id}`);
    } catch (err) {
      logger.error("[ServiceEntry] submit:", err?.message);
      toast.error(err.response?.data?.detail || "Failed to create job");
    } finally { setLoading(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
            New Service Job
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Fill in the details to register a new repair job</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>

        {/* ── TOP ROW: Customer (left) + Fault & Payment (right) ──────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">

          {/* LEFT — Customer Details */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <SectionHeader icon={User} title="Customer Details" subtitle="Who is bringing the charger?"/>
              <div className="space-y-4">
                <F label="Full Name" required error={errors.name}>
                  <Input placeholder="e.g. Rahul Sharma" value={custName}
                    onChange={e => setCustName(e.target.value)}
                    className={`h-9 text-sm ${errors.name ? "border-rose-400" : ""}`} data-testid="se-name"/>
                </F>
                <F label="Phone Number" required error={errors.phone}>
                  <Input placeholder="e.g. 9876543210" value={phone}
                    onChange={e => handlePhone(e.target.value)}
                    className={`h-9 text-sm ${errors.phone ? "border-rose-400" : ""}`} data-testid="se-phone"/>
                </F>
                <F label="Alternate Number">
                  <Input placeholder="Alt contact (optional)" value={altPhone}
                    onChange={e => setAltPhone(e.target.value)} className="h-9 text-sm"/>
                </F>
                {existingCustomer && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                      <AlertTriangle size={13}/> Existing customer — {existingCustomer.total} previous job{existingCustomer.total!==1?"s":""}
                    </p>
                    {existingCustomer.jobs?.slice(0,3).map(j => (
                      <a key={j.id} href={`/jobs/${j.id}`} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-900 mt-1 group">
                        <span className="font-mono">{j.job_no}</span><span>{j.current_status}</span>
                        <ExternalLink size={9} className="opacity-0 group-hover:opacity-100"/>
                      </a>
                    ))}
                  </div>
                )}
                <F label="Address">
                  <Textarea placeholder="Customer address (optional)" value={address}
                    onChange={e => setAddress(e.target.value)} className="text-sm resize-none" rows={2}/>
                </F>
              </div>
            </CardContent>
          </Card>

          {/* RIGHT — Fault & Notes + Payment */}
          <div className="space-y-5">
            <Card className="rounded-2xl border border-slate-200/70 bg-white">
              <CardContent className="p-5">
                <SectionHeader icon={Wrench} title="Fault & Notes" subtitle="What is the problem?"/>
                <div className="space-y-3">
                  <F label="Fault Description">
                    <Textarea placeholder="Describe the fault..." value={faultDesc}
                      onChange={e => setFaultDesc(e.target.value)} className="text-sm resize-none" rows={2}/>
                  </F>
                  <F label="Technical Notes">
                    <Textarea placeholder="Technical observations..." value={techNotes}
                      onChange={e => setTechNotes(e.target.value)} className="text-sm resize-none" rows={2}/>
                  </F>
                  <F label="Repair Notes">
                    <Textarea placeholder="What was repaired / replaced..." value={repairNotes}
                      onChange={e => setRepairNotes(e.target.value)} className="text-sm resize-none" rows={2}/>
                  </F>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-slate-200/70 bg-white">
              <CardContent className="p-5">
                <SectionHeader icon={CreditCard} title="Payment" subtitle="Billing information"/>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Total Amount (₹)">
                      <Input type="number" min="0" placeholder="0.00" value={totalAmt}
                        onChange={e => setTotalAmt(e.target.value)} className="h-9 text-sm tabular-nums" data-testid="se-total-amt"/>
                    </F>
                    <F label="Amount Paid (₹)">
                      <Input type="number" min="0" placeholder="0.00" value={paidAmt}
                        onChange={e => setPaidAmt(e.target.value)} className="h-9 text-sm tabular-nums"/>
                    </F>
                  </div>
                  <F label="Payment Method">
                    <Select value={payMethod} onValueChange={setPayMethod}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue/></SelectTrigger>
                      <SelectContent>{["Cash","UPI","Card","Bank Transfer"].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  {parseFloat(totalAmt) > 0 && (
                    <div className="space-y-1 p-3 bg-slate-50 rounded-xl">
                      <div className="flex justify-between text-xs text-slate-600"><span>Total</span><span className="tabular-nums font-medium">₹{parseFloat(totalAmt||0).toLocaleString("en-IN")}</span></div>
                      <div className="flex justify-between text-xs text-emerald-700"><span>Paid</span><span className="tabular-nums font-medium">₹{parseFloat(paidAmt||0).toLocaleString("en-IN")}</span></div>
                      <Separator className="my-1"/>
                      <div className="flex justify-between text-xs font-semibold text-rose-600"><span>Due</span><span className="tabular-nums">₹{due.toLocaleString("en-IN")}</span></div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ── CHARGER DETAILS (per-card) ────────────────────────────────────── */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-green-600 flex items-center justify-center">
                <Zap size={15} className="text-white"/>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>Charger Details</h3>
                <p className="text-xs text-slate-400">{chargers.length} charger{chargers.length!==1?"s":""} — add as many as needed</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {chargers.map((c, i) => {
              const eligible = isEligibleForNew(c.purchase_month, c.purchase_year);
              return (
                <Card key={i} className="rounded-2xl border border-slate-200/70 bg-white">
                  <CardContent className="p-5">
                    {/* Card header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center">{i+1}</span>
                        <span className="text-sm font-semibold text-slate-700">Charger #{i+1}</span>
                        {c.xp_charger_status && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                            c.xp_charger_status === "returned" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-green-50 text-green-700 border-green-200"
                          }`}>
                            {c.xp_charger_status === "returned" ? "🔄 XP Returned" : "🎁 XP New Given"}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => duplicateCharger(i)} title="Duplicate"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-green-600 hover:bg-green-50 transition-colors"><Copy size={13}/></button>
                        {chargers.length > 1 && (
                          <button type="button" onClick={() => removeCharger(i)} title="Remove"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"><Trash2 size={13}/></button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Charger Type Segment */}
                      <div className="sm:col-span-2">
                        <Label className="text-xs font-medium text-slate-700">Charger Type <span className="text-rose-500">*</span></Label>
                        <div className="flex gap-2 mt-1.5">
                          {CHARGER_SEGMENTS.map(seg => (
                            <button key={seg.label} type="button"
                              onClick={() => { updateCharger(i, "charger_type", seg.label); updateCharger(i, "charger_spec", ""); }}
                              className={`flex-1 py-2.5 px-2 rounded-xl text-xs font-semibold border transition-all leading-tight ${
                                c.charger_type === seg.label
                                  ? "bg-green-600 text-white border-green-600 shadow-sm"
                                  : "bg-white text-slate-600 border-slate-200 hover:border-green-400 hover:text-green-700 hover:bg-green-50"
                              }`}>
                              <span className="block text-base mb-0.5">{seg.icon}</span>{seg.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Model dropdown */}
                      {c.charger_type && (
                        <div className="sm:col-span-2">
                          <Label className="text-xs font-medium text-slate-700">Model <span className="text-rose-500">*</span></Label>
                          <Select value={c.charger_spec} onValueChange={v => updateCharger(i, "charger_spec", v)}>
                            <SelectTrigger className="mt-1.5 h-10 text-sm font-semibold">
                              <SelectValue placeholder={`Select ${c.charger_type} model…`}/>
                            </SelectTrigger>
                            <SelectContent>
                              {(SEGMENT_MODELS[c.charger_type]||[]).map(m => (
                                <SelectItem key={m} value={m}><span className="font-mono font-semibold text-green-700">{m}</span></SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {c.charger_spec && (
                            <p className="text-xs text-slate-500 mt-1">Selected: <span className="font-semibold text-green-700">{c.charger_type} — {c.charger_spec}</span></p>
                          )}
                        </div>
                      )}

                      {/* Serial Number */}
                      <F label="Serial Number">
                        <Input placeholder="e.g. XPLI43900" value={c.serial_number}
                          onChange={e => updateCharger(i, "serial_number", e.target.value)} className="h-10 text-sm font-mono"/>
                      </F>

                      {/* Body Type */}
                      <F label="Body Type">
                        <Select value={c.body_type} onValueChange={v => updateCharger(i, "body_type", v)}>
                          <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Select body type"/></SelectTrigger>
                          <SelectContent>{BODY_TYPES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                        </Select>
                      </F>

                      {/* Purchase Month + Year in ONE ROW */}
                      <div className="sm:col-span-2">
                        <Label className="text-xs font-medium text-slate-700">Purchase Date</Label>
                        <div className="grid grid-cols-2 gap-3 mt-1.5">
                          <Select value={c.purchase_month} onValueChange={v => { updateCharger(i, "purchase_month", v); }}>
                            <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Month"/></SelectTrigger>
                            <SelectContent className="max-h-52">
                              {MONTHS.map((m, idx) => <SelectItem key={idx+1} value={String(idx+1)}>{m}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={c.purchase_year} onValueChange={v => { updateCharger(i, "purchase_year", v); }}>
                            <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Year"/></SelectTrigger>
                            <SelectContent className="max-h-52">{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* ── XP Charger Section ────────────────────────────────── */}
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">XP Charger</span>
                        <span className="text-[10px] text-slate-400">(warranty replacement)</span>
                      </div>
                      <div className="flex gap-3 flex-wrap">
                        {/* Returned to Service Centre */}
                        <button type="button"
                          onClick={() => updateCharger(i, "xp_charger_status", c.xp_charger_status === "returned" ? "" : "returned")}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                            c.xp_charger_status === "returned"
                              ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                              : "bg-orange-50 text-orange-700 border-orange-200 hover:border-orange-400"
                          }`}>
                          <RotateCcw size={15}/> Returned to Service Centre
                        </button>

                        {/* New Given to Customer */}
                        {eligible ? (
                          <button type="button"
                            onClick={() => updateCharger(i, "xp_charger_status", c.xp_charger_status === "new_given" ? "" : "new_given")}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                              c.xp_charger_status === "new_given"
                                ? "bg-green-600 text-white border-green-600 shadow-sm"
                                : "bg-green-50 text-green-700 border-green-200 hover:border-green-400"
                            }`}>
                            <Gift size={15}/> New Given to Customer
                          </button>
                        ) : (c.purchase_month && c.purchase_year) ? (
                          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-slate-500 bg-slate-100 border-2 border-slate-200">
                            <Info size={14}/> Not eligible — warranty replacement period has expired
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs text-slate-400 bg-slate-50 border border-slate-200">
                            Set purchase date above to check eligibility
                          </div>
                        )}
                      </div>

                      {/* Courier sections — only when XP Charger is selected */}
                      {c.xp_charger_status && (
                        <div className="mt-4 space-y-4">
                          {/* Outgoing courier */}
                          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                            <div className="flex items-center gap-2 mb-1">
                              <Truck size={14} className="text-green-700"/>
                              <p className="text-xs font-bold text-green-800 uppercase tracking-wide">New Charger Dispatched to Customer</p>
                            </div>
                            <p className="text-xs text-green-600 mb-2">Fill in once new charger is shipped to customer</p>
                            <CourierFields value={c.outgoing_courier} outgoing={true}
                              onChange={v => updateCharger(i, "outgoing_courier", v)} />
                          </div>
                          {/* Incoming courier */}
                          <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                            <div className="flex items-center gap-2 mb-1">
                              <Truck size={14} className="text-orange-700"/>
                              <p className="text-xs font-bold text-orange-800 uppercase tracking-wide">Original Charger Return from Customer</p>
                            </div>
                            <p className="text-xs text-orange-600 mb-2">Fill in when customer sends original charger back</p>
                            <CourierFields value={c.incoming_courier} outgoing={false}
                              onChange={v => updateCharger(i, "incoming_courier", v)} />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ── Photo Upload ──────────────────────────────────────── */}
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <Label className="text-xs font-medium text-slate-700">
                        Charger {i+1} Photo <span className="text-rose-500">*</span>
                        <span className="text-slate-400 font-normal ml-1">(max 20 MB)</span>
                      </Label>

                      {/* Sample reference */}
                      <div className="mt-2 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex-shrink-0 w-16 h-16 border-2 border-amber-300 rounded-lg bg-white flex flex-col items-center justify-center text-center p-1">
                          <p className="text-[7px] font-bold text-slate-700 leading-tight">XSTRONG</p>
                          <p className="text-[6px] text-slate-500">Model: 67.2V/6A</p>
                          <p className="text-[6px] text-slate-500">S/N: XPLI43900</p>
                          <div className="w-8 h-8 mt-0.5 bg-slate-800 rounded-sm flex items-center justify-center">
                            <span className="text-[8px] text-white">▦</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-amber-800">Please upload a clear photo of the charger label sticker</p>
                          <p className="text-xs text-amber-700 mt-0.5">The label should clearly show: <strong>Model No</strong>, <strong>Serial Number</strong>, and <strong>QR code</strong></p>
                        </div>
                      </div>

                      <div className="mt-2 flex items-start gap-3 flex-wrap">
                        {c.photo_url ? (
                          <div className="relative group">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt={"Charger " + (i+1)}
                              className="w-24 h-24 object-cover rounded-xl border-2 border-green-200 shadow-sm"/>
                            <button type="button" onClick={() => updateCharger(i, "photo_url", "")}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-500 text-white rounded-full text-xs font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                          </div>
                        ) : null}
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" className="hidden"
                            onChange={e => handlePhoto(i, e.target.files?.[0])} />
                          <div className={`h-10 px-4 flex items-center gap-2 rounded-xl border text-sm transition-colors ${
                            photoUploading[i]
                              ? "border-green-300 bg-green-50 text-green-600"
                              : errors[`photo_${i}`]
                              ? "border-rose-400 bg-rose-50 text-rose-600"
                              : "border-slate-200 bg-white text-slate-600 hover:border-green-400 hover:text-green-700"
                          }`}>
                            {photoUploading[i]
                              ? <><Loader2 size={14} className="animate-spin"/> Uploading…</>
                              : <><Camera size={14}/> {c.photo_url ? "Change Photo" : "Upload Photo"}</>}
                          </div>
                        </label>
                      </div>
                      {errors[`photo_${i}`] && !c.photo_url && (
                        <p className="text-xs text-rose-500 mt-1">Photo is required — please upload a photo of the charger label</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Add Another Charger */}
          <button type="button" onClick={addCharger}
            className="w-full mt-3 flex items-center justify-center gap-2 text-sm text-green-700 hover:text-green-800 border border-dashed border-green-300 hover:border-green-500 rounded-xl py-3 transition-colors bg-green-50/30 hover:bg-green-50">
            <Plus size={15}/> Add Another Charger
          </button>
        </div>

        {/* ── SUBMIT ───────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={loading}>Cancel</Button>
          <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white min-w-[160px]"
            disabled={loading} data-testid="se-submit">
            {loading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Creating...</>
              : <><ChevronRight className="w-4 h-4 mr-1"/>Create Service Job</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
